/**
 * @module app.um
 */

import { autoinject, observable } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { ISmartTaskBinding, ITaskContainer } from 'app/core/components/tasks/task';
import { ITasksConfig } from 'app/core/models/essette-interfaces';
import { validate } from 'app/shared/elements/ess-rule';
import { MemberPlanSelect } from 'app/cd/components/member-plan-select';
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { AuthService } from 'app/um/services/auth-service';
import { EssForm } from 'app/shared/elements/forms/ess-form';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { guid } from 'app/shared/lib/guid';
import { NamespaceOptions } from 'app/shared/lib/i18n';


@autoinject()
export class SmartAuth extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @observable
    public memberSearchFilters: IMemberSearchFilters = {};

    public isProviderPortal: boolean = false;

    protected auths: UM.Auth[] = [];

    protected memberId!: string;
    protected originCode!: string;
    private authId: string | null = null;

    protected task!: Core.Task;
    protected targetId!: string;
    protected tasksConfig: ITasksConfig = {};
    protected readonly: boolean = false;

    protected $isValid!: boolean;
    protected validateForm = validate;

    private container!: ITaskContainer;

    protected hasAuthOwner: boolean = false;
    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;
    protected shouldRedirect: boolean = false;
    protected hasAuthRoute = false;

    protected i18nNamespace: Extract<NamespaceOptions, NamespaceOptions.CD | NamespaceOptions.PP> = NamespaceOptions.CD;

    protected id: guid = guid();


    ////****************** Element References ***************************************////
    public memberPlanSelect!: MemberPlanSelect;
    public $form!: EssForm;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AuthService,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
        if(this.env.application.code === 'Portal.Providers') {
            this.isProviderPortal = true;
            this.i18nNamespace = NamespaceOptions.PP;
        }
    }

    protected async activate(params: ISmartTaskBinding): Promise<void> {
        if(!params || !params.container || !params.targetId || !params.task || !params.task.id || !params.tasksConfig || !params.tasksConfig.smartTasksConfig || !params.tasksConfig.smartTasksConfig.notesConfig) {
            throw TypeError(`${SmartAuth.name} Component Model not Bound.`);
        }

        this.targetId = params.targetId;
        this.tasksConfig = params.tasksConfig;
        this.memberId = params.relatedModels && params.relatedModels['CD.Member'] || '';
        this.originCode = params.tasksConfig.smartTasksConfig && params.tasksConfig.smartTasksConfig.moduleTaskCode || '';

        this.state = params.state || this.state;

        this.task = params.task;
        this.container = params.container;
        this.readonly = params.readonly;

        await this.checkPermissions();
    }

    protected bind(): void {
        this.loadAuths();
    }

    protected attached(): void {
        if(this.memberId) {
            this.memberPlanSelect.searchMemberPlan(this.memberId);
        }
    }


    ////****************** Events ***************************************************////
    protected async createAuthSuccess(): Promise<void> {
        if(this.container) {
            await this.container.completeTask(!this.shouldRedirect);

            if(this.shouldRedirect && this.authId) {
                this.router.navigateToRoute('um.auth', { authId: this.authId });
            } else {
                this.task.isCompleted = true;
                this.loadAuths();
            }
        }
    }

    protected async submit(redirect: boolean = false): Promise<boolean> {
        this.shouldRedirect = redirect;
        return this.$form.submit();
    }

    protected async openAuth() {
        if(this.auths.length > 0) {
            this.router.navigateToRoute('um.auth', { authId: this.auths[0].id });
        }
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasAuthOwner = this.accountContext.hasPermission('UM.Auth.Duty.Owner');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
    }


    ////****************** Service Calls ********************************************////
    private async loadAuths(): Promise<void> {
        if(!this.task.id) { return; }

        try {
            this.state.beginLoad();

            const includes = ['class', 'class.subclass', 'type', 'assignedTo'];
            const response = await this.service.loadAuthsCreatedByTask(this.task.id, { all: true }, includes);
            this.auths = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
